import {PolymerElement, html} from '@polymer/polymer';
import {timeOut} from '@polymer/polymer/lib/utils/async';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import '@polymer/polymer/lib/elements/dom-if';
import '@material/mwc-ripple';
import {ScopeBinding} from './behaviors/keli-binding-behavior';
import {ActionBehavior} from './behaviors/action-behavior';
import {HotkeyBehavior} from './behaviors/hotkey-behavior';
import {TooltipBehavior} from './behaviors/tooltip-behavior';
import {RippleBehavior} from './behaviors/ripple-behavior';
import {DropdownBehavior} from './behaviors/dropdown-behavior.js';
import {SyncActionBrowseBehavior} from './behaviors/sync-action-browse-behavior';
import {backgroundColorValue, desiredTextColor, foregroundColorValue} from '../javascripts/modules/colors.js';

class KeliButton extends ScopeBinding(SyncActionBrowseBehavior(TooltipBehavior(HotkeyBehavior(ActionBehavior(RippleBehavior(DropdownBehavior(PolymerElement))))))) {
  static get template() {
    return html`
      <style>
        :host {
          display: inline-flex;
          flex-direction: row;
          -webkit-touch-callout: none;
          user-select: none;
          cursor: pointer;
          box-sizing: border-box;
          text-transform: uppercase;
          text-align: center;
          outline: none;
          border-radius: 2px;
          position: relative;
          color: var(--keli-button-text-color, var(--keli-text));
          background-color: var(--keli-button-background-color, transparent);
          transition: box-shadow var(--keli-transition-medium);
          font-size: var(--keli-button-font-size, 1em);
          --mdc-ripple-color: var(--keli-text);
        }

        :host(:hover:not(.disabled)) {
          background-color: var(--keli-button-background-color, var(--keli-action-hover-background));
        }

        :host(:focus) #action {
          outline: 3px solid var(--keli-field-border-color-active);
        }

        :host(.raised) {
          background-color: var(--keli-button-background-color, var(--keli-theme-white), transparent);
          box-shadow: var(--keli-shadow-z1);
        }

        :host(.raised:active), :host(.raised:focus) {
          box-shadow: var(--keli-shadow-z2);
        }
        
        :host(.disabled) {
          color: var(--keli-text-subtle) !important;
        }
        
        #action {
          display: flex;
          flex-direction: column;
          position: relative;
          padding: var(--keli-button-padding, .7em 1em .2em 1em);
          min-height: 100%;
          box-sizing: border-box;
        }

        #split-action {
          width: 1.7em;
          border-left: 1px solid var(--keli-button-border-color, currentColor);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        #caption {
          white-space: nowrap;
          display: block;
          font-size: .9em;
          line-height: 1.4em;
        }

        #caption.hidden {
          display: none;
        }

        sl-icon {
          margin: 0 auto;
          text-align: center;
          font-size: 1.3em;
        }

        #shortcut {
          font-size: .7em;
          text-align: center;
          white-space: nowrap;
          color: var(--keli-button-text-color, var(--keli-text-subtle));
        }
      </style>

      <div id="action" on-click="activate">
        <template is="dom-if" if="[[formattedCaption]]">
          <div id="caption">[[formattedCaption]]</div>
        </template>
        <template is="dom-if" if="[[formattedIcon]]">
          <sl-icon name="[[formattedIcon]]"></sl-icon>
        </template>
        <div id="shortcut">[[formattedShortcutKey]]</div>
        <mwc-ripple id="ripple"></mwc-ripple>
      </div>

      <template is="dom-if" if="[[splitButton]]">
        <div id="split-action" on-click="activateSplitAction">
          <sl-icon name="menu-down"></sl-icon>
        </div>
      </template>`;
  }

  static get is() {
    return 'keli-button';
  }

  static get properties() {
    return {
      background: {type: String, observer: 'backgroundChanged', scopevar: true},
      caption: {type: String, scopevar: true},
      color: {type: String, observer: 'colorChanged', scopevar: true},
      design: {type: String, observer: 'designChanged', scopevar: true},
      focusOnActivation: {type: Boolean, value: true},
      fontScale: {type: String, scopevar: true, observer: 'fontScaleChanged'},
      formattedCaption: {type: String, computed: 'computeFormattedCaption(icon, caption)'},
      formattedIcon: {type: String, computed: 'computeFormattedIcon(icon)'},
      formattedShortcutKey: {type: String, computed: 'computeFormattedShortcutKey(showHotkey, hotkeys, shortcutKey)'},
      hidden: {type: Boolean, reflectToAttribute: true, observer: 'hiddenChanged'},
      hotkeys: {type: Array, observer: 'hotkeysChanged'},
      icon: {type: String, scopevar: true},
      keliControlTypes: {type: Array, value: ['button']},
      shortcutKey: {type: String},
      showHotkey: {type: Boolean, scopevar: true},
      skip: {type: Boolean, scopevar: true},
      splitButton: {type: Boolean},
      splitContentObserver: {type: Object},
      submit: {type: Object}, // `submit` is an object of additional information to include with action event.
      tooltipText: {type: String, computed: 'computeTooltipText(caption, icon, showHotkey, hotkeys, shortcutKey)'}
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('focusElement', this.handleFocusElement);
    this.addEventListener('blur', this.blurButton);

    this.registerHotkey(KeliInterface.Hotkeys.button.choose, {
      down: event => {
        event.stopImmediatePropagation();
        event.preventDefault();
        this.activate(event);
      }
    });

    this.addTooltip(this, 'tooltipText');

    // Watch for split button items
    let additionMutations = false;
    this.splitContentObserver = new MutationObserver((mutations) => {
      this.splitButton = !!this.childElementCount;

      for (const mutation of mutations) {
        if (mutation.target === this && mutation.type === 'attributes') {
          continue;
        }

        if (mutation.target !== this && mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          this.dropdownElement.removeContent(`#${mutation.target.id}`);
          continue;
        }

        if (mutation.removedNodes.length) {
          for (const element of mutation.removedNodes) {
            this.dropdownElement.removeContent(`#${element.id}`);
          }
          continue;
        }

        additionMutations = true;
      }

      this._splitContentObserverJob = Debouncer.debounce(this._splitContentObserverJob, timeOut.after(20), () => {
        if (additionMutations) {
          const buttonChildren = Array.from(this.querySelectorAll(':not(style)')).filter(child => !child.hasAttribute('hidden'));
          const dropdownChildren = Array.from(this.dropdownElement.queryContent(':not(style)'));

          const finalElements = [];
          for (const element of buttonChildren) {
            let clone = dropdownChildren.find(child => child.id === element.id);
            if (!clone) {
              clone = element.cloneNode();
              clone.keliTargetRegistrationElement = this;
              clone.hotkeys = element.hotkeys;
            }
            finalElements.push(clone);
          }
          this.dropdownElement.replaceContentElements(finalElements);
          additionMutations = false;
        }
      });
    });
    this.splitContentObserver.observe(this, {attributes: true, childList: true, subtree: true});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('focusElement', this.handleFocusElement);
    this.removeEventListener('blur', this.blurButton);

    if (this.splitContentObserver) {
      this.splitContentObserver.disconnect();
      this.splitContentObserver = null;
    }
  }

  activate(event) {
    if (this.disabled) {
      return;
    }

    // Prevent more than one action in a sequence
    if (event?.repeat || event?.detail > 1) {
      return;
    }

    let eventArguments = [event];
    if (this.submit) {
      eventArguments.push(this.submit);
    }
    this._activate.apply(this, eventArguments);
    if (!event || !event.detail || typeof event.detail.x === 'undefined' || typeof event.detail.y === 'undefined') {
      this.simulateRipple();
    }

    if (this.focusOnActivation && (!event || event.focusOnActivation !== false)) {
      this.handleFocusElement(event);
    }

    // Emit an event to allow external binding to button activations.
    this.dispatchEvent(new CustomEvent('activate'));
  }

  activateSplitAction(event) {
    if (this.disabled) {
      return;
    }
    this.dropdownElement.toggleElement = event.currentTarget;
    if (this.dropdownElement.visible()) {
      this.dropdownElement.hide();
    } else {
      this.dropdownElement.show();
    }
  }

  computeFormattedCaption(icon, caption) {
    return (icon ? undefined : caption);
  }

  computeFormattedIcon(icon) {
    return KeliInterface.Styles.parseIconName(icon);
  }

  computeFormattedShortcutKey(showHotkey, hotkeys, shortcutKey) {
    if (KeliInterface.isTouchDevice) {
      return '';
    }
    if (showHotkey === false) {
      return '';
    }

    let chosenKey = undefined;
    if (hotkeys && hotkeys.length) {
      hotkeys.forEach(hotkey => {
        if (!chosenKey && !KeliInterface.Hotkeys.alphaNumerics.includes(hotkey)) {
          chosenKey = hotkey;
        }
      });
    }

    if (!chosenKey) {
      chosenKey = shortcutKey;
    }

    if (!KeliInterface.isTouchDevice) {
      return formatHotkeyAsText(chosenKey);
    }
  }

  computeTooltipText(caption, icon, showHotkey, hotkeys, shortcutKey) {
    if (KeliInterface.isTouchDevice) {
      return '';
    }
    let text = '';
    if (icon) {
      text += caption;
    }
    const hotkeyList = [];
    if (hotkeys?.length) {
      for (const hotkey of hotkeys) {
        if (!KeliInterface.Hotkeys.alphaNumerics.includes(hotkey.toString().toLowerCase())) {
          hotkeyList.push(hotkey);
        }
      }
    }
    if (shortcutKey) {
      hotkeyList.push(shortcutKey);
    }
    if (showHotkey !== false) {
      hotkeyList.shift();
    }
    if (hotkeyList.length) {
      text += (text.length > 0 ? ' ' : '') + formatHotkeyAsText(hotkeyList);
    }
    return text;
  }

  designChanged(newValue, oldValue) {
    oldValue && this.classList.remove(oldValue);
    newValue && this.classList.add(newValue);
  }

  disabledChanged(newValue) {
    this.classList.toggle('disabled', !!newValue);
    if (this.$.ripple) {
      this.$.ripple.disabled = newValue;
    }
    this.dispatchEvent(new CustomEvent('panelElementAttributeChanged', {
      bubbles: true, composed: true,
      detail: {element: this, attribute: 'disabled'}
    }));
  }

  backgroundChanged(newValue) {
    const backgroundColor = backgroundColorValue(newValue);
    this.updateStyles({
      '--keli-button-background-color': backgroundColor,
      '--keli-button-text-color': foregroundColorValue(this.color, null, true, backgroundColor) || desiredTextColor(backgroundColor)
    });
  }

  colorChanged(newValue) {
    afterNextRender(this, () => {
      this.updateStyles({'--keli-button-text-color': foregroundColorValue(newValue, this)});
    });
  }

  fontScaleChanged(newValue) {
    this.updateStyles({'--keli-button-font-size': `${newValue || 1}em`});
  }

  hotkeysChanged(newValue, oldValue) {
    if (oldValue && oldValue.length) {
      oldValue.forEach(hotkey => {
        this.dispatchEvent(new CustomEvent('unregisterPanelHotkey', {
          bubbles: true,
          composed: true,
          detail: {'keys': hotkey}
        }));
      });
    }

    if (newValue && newValue.length) {
      newValue.forEach(hotkey => {
        this.dispatchEvent(new CustomEvent('registerPanelHotkey', {
          bubbles: true,
          composed: true,
          detail: {'keys': hotkey, 'scope': this}
        }));
      });
    }
  }

  activateShortcutKey(event) {
    this.activate(event);
  }

  hiddenChanged(newValue) {
    if (newValue) {
      this.shortcutKey = undefined;
    }
  }

  handleFocusElement(event) {
    if (KeliInterface.isTouchDevice) {
      return;
    }
    event.stopImmediatePropagation();

    this.setAttribute('tabindex', '0');
    this.focus();
    this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
      bubbles: true,
      composed: true,
      detail: {element: this}
    }));
    this.resetHotkeyState();
  }

  blurButton() {
    this.removeAttribute('tabindex');
  }
}

customElements.define(KeliButton.is, KeliButton);
export {KeliButton};
